import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Edit } from '@ui/components/icons';
import { LongShortBadge } from '@ui/components/LongShortBadge';
import { TextTooltip } from '@ui/components/TextTooltip';
import { DEFAULT_UNDEFINED_NUMBER_DISPLAY } from '@ui/constants/uiConfig';
import { MarketTimeIndicator } from '@ui/features/RWA/components/MarketTimeIndicator';
import { Button } from '@ui/lib/park/ui/button';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { calculatePositionTotalPoints } from '@ui/services/contract/hooks/GenesisAirdrop/points';
import { PositionBasicPoints } from '@ui/services/contract/hooks/GenesisAirdrop/useBatchPositionBasicPoints';
import {
  getActiveSlOrders,
  getActiveTpOrders,
  getDecreaseOrderTriggerPrice,
} from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/selectors';
import type { DecreaseOrderRow } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { useTranslation } from 'next-i18next';
import { Box, Flex, HStack, Stack, styled } from 'styled-system/jsx';

import { MarginOverlay } from '../MarginOverlay/MarginOverlay';
import { TradeLeverageBadge } from '../LeverageBadge';
import { Row } from '../Row';
import { SizeUsd } from '../SizeUsd';
import { CollateralTooltipContent } from './CollateralTooltipContent';
import { InitialCollateral } from './InitialCollateral';
import { LiquidationPrice } from './LiquidationPrice';
import { MarketPrice } from './MarketPrice';
import { NetValue } from './NetValue';
import { PointsTooltipContent } from './PointsTooltipContent';
import { PositionCloseButton } from './PositionCloseButton';
import { PositionPnl } from './PositionPnl';
import { PositionShareOverlay } from './PositionShareOverlay';
import { PositionTpSlEditButton } from './PositionTpSlEditButton';
import { getSidePillProps, valueStyle } from './styles';
import { PositionMarketData } from './types';

export const CardMobile = ({
  position,
  activeDecreaseOrders,
  priceDisplayDecimals,
  isPendingPosition,
  marketData,
  points,
}: {
  position: Position;
  activeDecreaseOrders?: DecreaseOrderRow[];
  priceDisplayDecimals: number;
  isPendingPosition: boolean;
  marketData?: PositionMarketData;
  points?: PositionBasicPoints;
}) => {
  const { t } = useTranslation();
  const totalPoints = points ? calculatePositionTotalPoints(points) : undefined;
  const tpOrders = getActiveTpOrders(activeDecreaseOrders);
  const slOrders = getActiveSlOrders(activeDecreaseOrders);
  const firstTpPrice = position.isMergedPosition
    ? getDecreaseOrderTriggerPrice(tpOrders[0])
    : position.takeProfit;
  const firstSlPrice = position.isMergedPosition
    ? getDecreaseOrderTriggerPrice(slOrders[0])
    : position.stopLoss;
  const isInCountdown =
    Date.now() <
    position.timestamp * 1000 + (position.pair.isDegenPair ? 60_000 : 180_000);

  return (
    <Box
      display={{ base: 'block', desktop: 'none' }}
      w="full"
      px="3"
      py="3"
      border="1px solid"
      borderColor="v2.border.default"
      bg="v2.bg.surface1"
      borderRadius="4px"
      mb="2"
    >
      <Flex justify="space-between" align="center" mb="2">
        <HStack gap="2">
          <styled.img
            src={position.pair.image}
            boxSize="6"
            borderRadius="6px"
          />
          <HStack gap="1" alignItems="center">
            <Text textStyle="v2.ui" color="v2.text.primary">
              {position.pair.baseName}
            </Text>
            <MarketTimeIndicator pairName={position.pair.pairName} />
          </HStack>
          <LongShortBadge
            ml={1}
            isLong={position.isLong}
            {...getSidePillProps(position.isLong)}
          />
          <TradeLeverageBadge genericTradeParams={position} />
        </HStack>

        <Box ml="3" minW="70px" textAlign="right">
          <PositionPnl
            position={position}
            css={{ justifyContent: 'flex-end' }}
            marketData={marketData}
          />
        </Box>
      </Flex>

      {/* Body：图片中的行样式 */}
      <Box borderTop="1px solid" borderTopColor="v2.border.hairline">
        <Row label={t('common.Size')}>
          <SizeUsd
            genericTradeParams={position}
            css={valueStyle}
            {...(marketData ? { marketPrice: marketData.pairMarketPrice } : {})}
          />
        </Row>

        <Row label={t('common.Collateral')}>
          <HStack gap={0.5} justify="flex-end">
            {isPendingPosition ? (
              DEFAULT_UNDEFINED_NUMBER_DISPLAY
            ) : (
              <TextTooltip
                content={
                  <CollateralTooltipContent
                    position={position}
                    marketData={marketData}
                  />
                }
              >
                <InitialCollateral
                  position={position}
                  {...(marketData
                    ? { marketPrice: marketData.collateralMarketPrice }
                    : {})}
                  data-cy="InitialCollateral"
                />
              </TextTooltip>
            )}
          </HStack>
        </Row>

        <Row label={t('common.Entry')}>
          <AmountUsd
            value={isPendingPosition ? undefined : position.entryPrice}
            css={valueStyle}
            opts={{ displayDecimals: priceDisplayDecimals }}
            data-cy="EntryPrice"
            data-cy-value={position.entryPrice}
          />
        </Row>

        <Row label={t('common.Market')}>
          <MarketPrice
            position={position}
            css={valueStyle}
            priceDisplayDecimals={priceDisplayDecimals}
            {...(marketData ? { marketPrice: marketData.pairMarketPrice } : {})}
          />
        </Row>

        <Row label={t('common.Net Value')}>
          <NetValue position={position} marketData={marketData} />
        </Row>

        <Row label={t('common.Points')}>
          <TextTooltip
            content={
              <PointsTooltipContent position={position} points={points} />
            }
          >
            {isInCountdown ? (
              <Text css={valueStyle}>0</Text>
            ) : totalPoints && totalPoints > 0 ? (
              <Text css={{ ...valueStyle, color: 'v2.long.default' }}>
                {totalPoints.toLocaleString()}
              </Text>
            ) : (
              <Text css={valueStyle}>—</Text>
            )}
          </TextTooltip>
        </Row>

        <Row label={t('common.Close Price')}>
          <HStack justify="flex-end">
            <Text textStyle="v2.meta" color="v2.text.muted">
              TP
            </Text>
            <AmountUsd
              css={{ ...valueStyle, color: 'v2.long.default' }}
              value={firstTpPrice}
              opts={{
                displayDecimals: priceDisplayDecimals,
                suffix: position.isMergedPosition
                  ? `(${tpOrders.length})`
                  : undefined,
              }}
            />
          </HStack>
          <HStack justify="flex-end">
            <Text textStyle="v2.meta" color="v2.text.muted">
              SL
            </Text>
            <AmountUsd
              css={{ ...valueStyle, color: 'v2.short.default' }}
              value={firstSlPrice}
              opts={{
                displayDecimals: priceDisplayDecimals,
                suffix: position.isMergedPosition
                  ? `(${slOrders.length})`
                  : undefined,
              }}
            />
          </HStack>
          <HStack justify="flex-end" css={valueStyle}>
            <Text textStyle="v2.meta" color="v2.text.muted">
              LIQ
            </Text>
            <LiquidationPrice
              position={position}
              marketData={marketData}
              priceDisplayDecimals={priceDisplayDecimals}
            />
          </HStack>
        </Row>
      </Box>

      {/* Footer：Operations（Share/Collateral/TP/SL/Close） */}
      <HStack
        gap="3"
        mt="3"
        justify="space-between"
        onClick={(e) => e.stopPropagation()}
      >
        <PositionShareOverlay position={position}>
          <Button variant="secondary" w="25%">
            {t('common.Share')}
          </Button>
        </PositionShareOverlay>
        {!isPendingPosition && (
          <MarginOverlay position={position}>
            <Button variant="secondary" w="25%">
              {t('common.Collateral')}
            </Button>
          </MarginOverlay>
        )}
        <PositionTpSlEditButton
          position={position}
          activeDecreaseOrders={activeDecreaseOrders}
        >
          <Button variant="secondary" w="25%">
            {t('common.TP/SL')}
          </Button>
        </PositionTpSlEditButton>
        <Box w="25%">
          <PositionCloseButton position={position} />
        </Box>
      </HStack>
    </Box>
  );
};
